import {
  Droplets,
  AlertTriangle,
  TrendingDown,
  CheckCircle,
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from "lucide-react";
import { motion } from "framer-motion";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getFlowHealth, getAnomalyCategory } from "../services";
import { useLocation } from "react-router-dom";
import { useDistrict } from "../utils/DistrictContext";
import { useTheme } from "../context/ThemeContext";
import dayjs from "dayjs";

function FlowHealth() {
  const [currentPage, setCurrentPage] = useState(1);
  const { theme, colors } = useTheme();
  const [loading, setLoading] = useState(true);
  const selectedDistrict = useDistrict();
  const location = useLocation();
  const [data, setData] = useState({
    leak: [],
    backflow: [],
    no_recent_flow: [],
  });

  const savedTab = sessionStorage.getItem("flowHealthTab");

  const defaultTab = savedTab || location.state?.tab || "leak";
  // const [activeTab, setActiveTab] = useState(defaultTab);
  const [activeTab, setActiveTab] = useState(() => {
    return sessionStorage.getItem("flowHealthTab") || "leak";
  });
  const [pageSize, setPageSize] = useState(window.innerWidth >= 1921 ? 20 : 5);
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");

  const filterBySearch = (rows) => {
    if (!search.trim()) return rows;

    const keyword = search.toLowerCase();

    return rows.filter((row) =>
      Object.values(row).some((value) => {
        if (!value) return false;
        return String(value).toLowerCase().includes(keyword);
      }),
    );
  };
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1921) {
        setPageSize(20);
      } else {
        setPageSize(5);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  useEffect(() => {
    setCurrentPage(1);
  }, [pageSize]);
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const cats = await getAnomalyCategory();

        const list = cats || [];
        const updatedList = [
          { class_display_name: "ALL", mizuwatch_code: "ALL" },
          ...list,
        ];

        setCategories(updatedList);

        const categoryFromNav = location.state?.category;

        if (categoryFromNav) {
          setSelectedCategory(categoryFromNav);
        } else {
          setSelectedCategory("ALL");
        }
      } catch (err) {
        console.error("Category API Error:", err);
      }
    };

    fetchCategories();
  }, [selectedDistrict, location.state]);

  useEffect(() => {
    if (!selectedCategory) return;
    const fetchFlowHealth = async () => {
      try {
        setLoading(true);
        const res = await getFlowHealth(selectedCategory);
        const flow = res?.data?.flow_data;
        setData({
          leak: flow?.leak || [],
          backflow: flow?.backflow || [],
          no_recent_flow: flow?.no_recent_flow || [],
        });
      } catch (err) {
        console.error("Flow Health API error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchFlowHealth();
  }, [selectedDistrict, selectedCategory]);

  const normalizeRow = (row, currentTab) => {
    // Collect all active messages
    const activeMessages = [
      row.leak_message,
      row.backflow_message,
      row.no_recent_message,
    ].filter((msg) => msg !== null && msg !== undefined && msg !== "-");

    let status = "success";

    // Logic for status icons
    if (currentTab === "leak" || currentTab === "backflow") {
      // If we are in the leak/backflow tabs, showing an error icon is appropriate
      status = activeMessages.length > 0 ? "error" : "success";
    } else if (currentTab === "no_recent_flow") {
      status = "success";
    }

    return {
      accountId: row.account_id,
      accountName: row.overided_name || row.name,
      meterType: row.name,
      meterId: row.meter_id,
      register: row.register_number || "-",
      previousWeek: `${row.pre_week_range}\n${row.pre_week}`,
      thisWeek: `${row.week_range}\n${row.week}`,
      gallons: row.total_water_loss,
      loss: row.total_price_for_loss,
      // Store as array so we can map over them in the JSX
      messages: activeMessages.length > 0 ? activeMessages : ["-"],
      status,
      rawMeterId: row.meter_id,
      rawWeekRange: row.week_range,
      leakStartDate: row.leak_start_date,
      leak_rate: row.leak_rate,
    };
  };
  const currentFullData = useMemo(() => {
    if (activeTab === "leak")
      return data.leak.map((row) => normalizeRow(row, "leak"));

    if (activeTab === "backflow")
      return data.backflow.map((row) => normalizeRow(row, "backflow"));

    if (activeTab === "no_recent_flow")
      return data.no_recent_flow.map((row) =>
        normalizeRow(row, "no_recent_flow"),
      );

    return [];
  }, [activeTab, data]);
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "asc",
  });
  const handleSort = (key) => {
    let direction = "asc";

    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }

    setSortConfig({ key, direction });
  };

  const filteredData = useMemo(() => {
    let data = filterBySearch(currentFullData);

    if (sortConfig.key) {
      data = [...data].sort((a, b) => {
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];

        if (aValue === undefined || bValue === undefined) return 0;

        if (typeof aValue === "number" && typeof bValue === "number") {
          return sortConfig.direction === "asc"
            ? aValue - bValue
            : bValue - aValue;
        }

        return sortConfig.direction === "asc"
          ? String(aValue).localeCompare(String(bValue))
          : String(bValue).localeCompare(String(aValue));
      });
    }

    return data;
  }, [currentFullData, search, sortConfig]);

  const totalPages = Math.ceil(filteredData.length / pageSize);
  const safePage = Math.min(currentPage, totalPages || 1);
  const startIndex = (safePage - 1) * pageSize;
  const paginatedData = filteredData.slice(startIndex, startIndex + pageSize);

  // const handleTabChange = (tab) => {
  //   setActiveTab(tab);
  //   setCurrentPage(1);
  // };
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);

    sessionStorage.setItem("flowHealthTab", tab);
  };

  useEffect(() => {
    const navTab = location.state?.tab;
    if (navTab) {
      setActiveTab(navTab);
      sessionStorage.setItem("flowHealthTab", navTab);
      setCurrentPage(1);

      // CRITICAL: Clear history state so it doesn't "lock" the tab
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, []);
  useEffect(() => {
    setCurrentPage(1);
  }, [search, activeTab, selectedCategory]);

  const TabGlow = () => (
    <motion.div
      className="absolute inset-0 bg-linear-to-r from-transparent via-[#00b8f1]/30 to-transparent"
      animate={{ x: ["-100%", "200%"] }}
      transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
    />
  );

  if (loading) {
    return (
      <main
        className="flex-1 p-6"
        style={{
          backgroundColor: theme === "white" ? "#ffffff" : colors.cardBg,
          color: colors.textSecondary,
        }}
      >
        Loading Flow Health...
      </main>
    );
  }

  const getPaginationRange = (current, total) => {
    const delta = 1;
    const range = [];
    const rangeWithDots = [];

    for (let i = 1; i <= total; i++) {
      if (
        i === 1 ||
        i === total ||
        (i >= current - delta && i <= current + delta)
      ) {
        range.push(i);
      }
    }

    let last = 0;
    for (let i of range) {
      if (i - last > 1) {
        rangeWithDots.push("...");
      }
      rangeWithDots.push(i);
      last = i;
    }

    return rangeWithDots;
  };

  return (
    <>
      <motion.div
        className="mb-6"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <h1
          className="text-4xl font-bold text-transparent bg-clip-text"
          style={{ backgroundImage: colors.gradient }}
        >
          Flow Health
        </h1>
        <p className="text-sm mt-1" style={{ color: colors.textSecondary }}>
          Monitor flow anomalies and water usage patterns
        </p>
      </motion.div>

      <div className="flex items-center justify-between mb-6 flex-wrap gap-5">
        <div className="flex gap-3 flex-wrap">
          {[
            { id: "leak", label: "Leak Detected", icon: AlertTriangle },
            { id: "backflow", label: "Backflow", icon: TrendingDown },
            { id: "no_recent_flow", label: "No Recent Flow", icon: Droplets },
          ].map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className="flex items-center gap-2 px-4 py-2.5 rounded-lg border relative overflow-hidden transition-all"
                style={{
                  border: `1px solid ${
                    activeTab === tab.id ? colors.primary : colors.border
                  }`,
                  backgroundColor:
                    activeTab === tab.id
                      ? `${colors.primary}15`
                      : theme === "white"
                        ? "#ffffff"
                        : `${colors.primary}08`,
                }}
              >
                {activeTab === tab.id && <TabGlow />}
                <Icon className="w-4 h-4" style={{ color: colors.primary }} />
                <span
                  className="text-sm font-semibold"
                  style={{ color: colors.text }}
                >
                  {tab.label}
                </span>
              </button>
            );
          })}
        </div>

        {/* RIGHT SIDE - Category + Search */}
        <div className="flex items-end gap-4 flex-wrap">
          <div className="max-[567px]:w-full">
            <label
              className="block text-xs mb-2 font-semibold"
              style={{ color: colors.primary }}
            >
              Category
            </label>

            <div className="relative">
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="px-3 py-2 pr-8 border rounded-lg text-sm appearance-none cursor-pointer w-56 max-[567px]:w-full"
                style={{
                  background: colors.background,
                  borderColor: colors.border,
                  color: colors.text,
                }}
              >
                {categories.map((cat) => (
                  <option
                    key={cat.mizuwatch_code || cat}
                    value={cat.class_display_name || cat}
                    style={{
                      backgroundColor:
                        theme === "white" ? "#ffffff" : colors.background,
                      color: colors.text,
                    }}
                  >
                    {cat.class_display_name || cat}
                  </option>
                ))}
              </select>

              <ChevronDown
                className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none"
                style={{ color: colors.textSecondary }}
              />
            </div>
          </div>

          {/* Search */}
          <input
            type="text"
            placeholder="Search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="px-4 py-2 rounded-lg w-72 outline-none max-[567px]:w-full"
            style={{
              backgroundColor: theme === "white" ? "#ffffff" : colors.cardBg,
              border: `1px solid ${colors.border}`,
              color: colors.text,
            }}
            onFocus={(e) =>
              (e.target.style.border = `1px solid ${colors.primary}`)
            }
            onBlur={(e) =>
              (e.target.style.border = `1px solid ${colors.border}`)
            }
          />
        </div>
      </div>

      <div
        className="rounded-xl overflow-hidden mb-5"
        style={{
          backgroundColor: theme === "white" ? "#ffffff" : colors.cardBg,
          border: `1px solid ${colors.border}`,
        }}
      >
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead
              style={{
                backgroundColor:
                  theme === "white" ? "#f8f9fa" : `${colors.primary}08`,
                borderTop: `1px solid ${colors.border}`,
                borderBottom: `1px solid ${colors.border}`,
              }}
            >
              <tr>
                {[
                  { label: "Account ID", key: "accountId" },
                  ...(selectedDistrict?.selectedDistrict !== "FB151"
                    ? [{ label: "Account Name", key: "accountName" }]
                    : []),
                  { label: "Meter Type", key: "meterType" },
                  { label: "Meter ID", key: "meterId" },
                  { label: "Register", key: "register" },
                  { label: "Previous Week", key: "previousWeek" },
                  { label: "This Week", key: "thisWeek" },
                  ...(activeTab === "leak"
                    ? [
                        { label: "Gallons", key: "gallons" },
                        { label: "Loss", key: "loss" },
                      ]
                    : []),
                  { label: "Message", key: "message" },
                ].map((h) => (
                  <th
                    key={h.label}
                    onClick={() => handleSort(h.key)}
                    className="text-left px-6 py-4 text-sm cursor-pointer"
                    style={{ color: colors.textSecondary }}
                  >
                    <span className="flex items-center gap-2 select-none">
                      {h.label}

                      {sortConfig.key === h.key ? (
                        sortConfig.direction === "asc" ? (
                          <ArrowUp
                            className="w-4 h-4"
                            style={{ color: colors.primary }}
                          />
                        ) : (
                          <ArrowDown
                            className="w-4 h-4"
                            style={{ color: colors.primary }}
                          />
                        )
                      ) : (
                        <ArrowUpDown
                          className="w-4 h-4"
                          style={{ color: colors.primary }}
                        />
                      )}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paginatedData.map((row, index) => (
                <tr
                  key={index}
                  className="border-b transition-all duration-200 cursor-pointer"
                  style={{ borderColor: colors.border }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.backgroundColor = `${colors.primary}08`)
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor = "transparent")
                  }
                  onClick={() => {
                    sessionStorage.setItem("flowHealthTab", activeTab);

                    let finalStartDate;

                    if (row.leakStartDate) {
                      // 1. If leak_start_date exists (e.g., "Mar 04, 2026 22:44")
                      finalStartDate = dayjs(
                        row.leakStartDate,
                        "MMM DD, YYYY HH:mm",
                      ).format("YYYY-MM-DD");
                    } else if (row.rawWeekRange) {
                      // 2. Fallback to start of week_range if leak_start_date is missing
                      const rangeParts = row.rawWeekRange.split(" - ");
                      finalStartDate = dayjs(
                        rangeParts[0],
                        "MMM DD, YYYY",
                      ).format("YYYY-MM-DD");
                    }

                    if (!finalStartDate) return;

                    // 3. Navigate with the specific leak start date
                    navigate(
                      `/meter-leak-details?category=${selectedCategory}&meter_id=${row.rawMeterId}&time_range=Current&selected_time=${finalStartDate}`,
                      {
                        state: {
                          water_loss_gallons: row.gallons,
                          price: row.loss,
                          leak_rate: row.leak_rate,
                        },
                      },
                    );
                  }}
                >
                  <td
                    className="px-6 py-4"
                    style={{ color: colors.textSecondary }}
                  >
                    {row.accountId}
                  </td>

                  {selectedDistrict?.selectedDistrict !== "FB151" && (
                    <td className="px-6 py-4" style={{ color: colors.text }}>
                      {row.accountName}
                    </td>
                  )}
                  <td
                    className="px-6 py-4"
                    style={{ color: colors.textSecondary }}
                  >
                    {row.meterType}
                  </td>
                  <td className="px-6 py-4" style={{ color: colors.text }}>
                    <span
                      className="font-bold underline decoration-1 underline-offset-4"
                      style={{ color: colors.primary }}
                    >
                      {row.meterId}
                    </span>
                  </td>
                  <td
                    className="px-6 py-4"
                    style={{ color: colors.textSecondary }}
                  >
                    {row.register}
                  </td>
                  <td
                    className="px-6 py-4 whitespace-pre-line"
                    style={{ color: colors.textSecondary }}
                  >
                    {row.previousWeek}
                  </td>
                  <td
                    className="px-6 py-4 whitespace-pre-line"
                    style={{ color: colors.textSecondary }}
                  >
                    {row.thisWeek}
                  </td>
                  {activeTab === "leak" && (
                    <>
                      {/* Gallons */}
                      <td
                        className="px-6 py-4 whitespace-pre-line"
                        style={{ color: colors.textSecondary }}
                      >
                        {row.gallons}
                      </td>

                      {/* Loss */}
                      <td
                        className="px-6 py-4 whitespace-pre-line"
                        style={{
                          color: "#ef4444",
                        }}
                      >
                        {row.loss ? String(row.loss).split(".")[0] : "-"}
                      </td>
                    </>
                  )}

                  <td className="px-6 py-4">
                    <div className="flex flex-col gap-2">
                      {row.messages.map((msg, msgIdx) => (
                        <div key={msgIdx} className="flex items-center gap-2">
                          {/* Determine icon based on specific message content if needed, 
            or use the row status */}
                          {row.status === "error" ? (
                            <AlertTriangle
                              className="w-3.5 h-3.5 shrink-0"
                              style={{ color: colors.warning || "#FF9800" }}
                            />
                          ) : (
                            <CheckCircle
                              className="w-3.5 h-3.5 shrink-0"
                              style={{ color: colors.success || "#4CAF50" }}
                            />
                          )}
                          <span
                            className="text-xs leading-tight"
                            style={{
                              color:
                                row.status === "error"
                                  ? colors.warning || "#FF9800"
                                  : colors.success || "#4CAF50",
                            }}
                          >
                            {msg}
                          </span>
                        </div>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div
          className="flex justify-between items-center gap-3 p-4 flex-wrap"
          style={{ borderTop: `1px solid ${colors.border}` }}
        >
          {/* Row Count Info */}
          <span className="text-[#BDB9B5] text-xs">
            Showing {startIndex + 1} to{" "}
            {Math.min(startIndex + pageSize, filteredData.length)} of{" "}
            {filteredData.length} entries
          </span>

          <div className="flex gap-4 items-center flex-wrap">
            {/* Page Numbers */}
            <div className="flex gap-2 items-center">
              <button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => p - 1)}
                className="px-3 py-1 rounded disabled:opacity-40"
                style={{
                  background: colors.rowAlt1,
                  color: colors.text,
                }}
              >
                {"<"}
              </button>

              {getPaginationRange(currentPage, totalPages).map((item, index) =>
                item === "..." ? (
                  <span
                    key={index}
                    className="px-2"
                    style={{ color: colors.textSecondary }}
                  >
                    ...
                  </span>
                ) : (
                  <button
                    key={index}
                    onClick={() => setCurrentPage(item)}
                    className="px-3 py-1 rounded border"
                    style={{
                      background:
                        currentPage === item ? colors.primary : colors.rowAlt1,
                      borderColor: colors.primary,
                      color: currentPage === item ? "#fff" : colors.text,
                    }}
                  >
                    {item}
                  </button>
                ),
              )}

              <button
                disabled={currentPage === totalPages || totalPages === 0}
                onClick={() => setCurrentPage((p) => p + 1)}
                className="px-3 py-1 rounded disabled:opacity-40"
                style={{
                  background: colors.rowAlt1,
                  color: colors.text,
                }}
              >
                {">"}
              </button>
            </div>

            {/* Dynamic Row Selector */}
            <div
              className="flex items-center gap-2 text-sm"
              style={{ color: colors.textSecondary }}
            >
              <span>Rows:</span>
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="rounded px-2 py-1 text-sm border outline-none cursor-pointer"
                style={{
                  background: theme === "white" ? "#ffffff" : colors.background,
                  borderColor: colors.border,
                  color: colors.text,
                }}
              >
                <option value={5}>5</option>
                <option value={9}>9</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default FlowHealth;
